/* eslint-disable max-len */
import { makeStyles, useTheme } from '@material-ui/core';
import { capitalizeAll, GenericMessageBox, GenericPanelComp, ModeTheme } from '@moderepo/mode-app-components';
import { EntityMetricsDefinition } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { MapContainer, Polyline, TileLayer, LayersControl, LayerGroup } from 'react-leaflet';
import { VehicleMarker, RouteLocation, TripData, MOBILITY_DEFAULT_ROUTE_LINE_THICKNESS, MOBILITY_VEHICLE_ANIMATION_DURATION, EventMarker,
    EventMarkerIconSetting, EventMetricsNameMapping } from '.';
import { ValueToEventStateMapping } from '../..';
import { useRitzTranslation } from '../../../../../utils/customHooks';
import { useSelectProject } from '../../../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../../../utils/ritzComponentsCollection';
import { MapFitBoundsHandler } from '../../../../misc';



const useStyle = makeStyles(() => {
    return {
        root: {
        },

        mapContainer: {
            width : '100%',
            height: 700,
        },

        noDataMessage: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
            height        : '100%',
        },
    };
}, { name: 'EntityVehicleRouteMapUIComponent', index: 1 });



export interface TripsRouteMapContainerProps {
    readonly projectId: number;
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly title?: string | undefined;
    readonly icon?: string |undefined;
    readonly mapContainerStyle?: CSSProperties | undefined;
    readonly tripsData: readonly TripData[];
    readonly showVehicleSpeed?: boolean | undefined;
    readonly lineThickness?: number | undefined;
    readonly speedColors: [string, ...string[]];
    readonly vehicleColor?: string | undefined;
    readonly vehicleSize?: number | undefined;
    readonly locationMetricsDefinition: EntityMetricsDefinition;
    readonly eventMetricsDefinition?: EntityMetricsDefinition | undefined;
    readonly eventMetricsNameMapping?: EventMetricsNameMapping | undefined;
    readonly eventTypeToEventStateMapping?: ValueToEventStateMapping | undefined;
    readonly eventMarkerIconsSettings?: EventMarkerIconSetting | undefined;
    readonly eventMarkerIconsSettingsByEventType?: {
        readonly [eventType: string]: EventMarkerIconSetting | undefined;
    } | undefined;
}


export const TripsRouteMapContainer: React.FC<TripsRouteMapContainerProps> = (props: TripsRouteMapContainerProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const theme = useTheme() as ModeTheme;
    const eventMetricsDefinition = props.eventMetricsDefinition;


    /**
     * Get all the route locations from the trip data as 1 flat array and cache them. We will use these to set the map's bounds to show the routes
     */
    const allLocations = useMemo(() => {
        return props.tripsData.map((tripData) => {
            return tripData.locations;
        }).flat().map((loc) => {
            return {
                lat: loc.lat,
                lng: loc.lon,
            };
        });
    }, [props.tripsData]);



    // The state to keep track of the selected trip and location. We will use this to position the vehicle marker.
    // This is only the pointers to the tripsData
    const [selectedTripAndLocationIndex, setSelectedTripAndLocationIndex] = useState<{
        readonly tripIndex: number;
        readonly locationIndex: number;
    }>({
        tripIndex: 0, locationIndex: 0,
    });


    /**
     * When tripData changed, reset the current trip/location index
     */
    useEffect(() => {
        setSelectedTripAndLocationIndex({
            tripIndex: 0, locationIndex: 0,
        });
    }, [props.tripsData]);



    // The Trip and Location object that is being selected based on the selectedTripAndLocationIndex
    const selectedTripAndLocationObjects = useMemo((): {
        readonly trip: TripData | undefined;
        readonly location: RouteLocation | undefined;
    } => {
        if (props.tripsData && props.tripsData.length > 0) {

            const { tripIndex, locationIndex } = selectedTripAndLocationIndex;
            const trip = tripIndex >= 0 && tripIndex < props.tripsData.length ? props.tripsData[tripIndex] : props.tripsData[0];

            // Get the selectedLocation from the selectedTrip's locations array or segments array or. If "showVehicleSpeed" is true AND if the
            // trip contains segments array then we get the location data from the segments. Else, the location data from the trip's locations array
            const location = props.showVehicleSpeed && trip.segments
                ? trip.segments[locationIndex]?.startLocation : trip.locations[locationIndex];

            return {
                trip,
                location,
            };
        }
        return {
            trip    : undefined,
            location: undefined,
        };
    }, [props.tripsData, props.showVehicleSpeed, selectedTripAndLocationIndex]);



    /**
     * For testing moving the vehicle to different location
     * TODO - Remove this
     */
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (props.tripsData) {
                setSelectedTripAndLocationIndex((currentValue) => {
                    return {
                        ...currentValue,
                        locationIndex: currentValue.locationIndex + 1,
                    };
                });
            }
        }, MOBILITY_VEHICLE_ANIMATION_DURATION);

        return () => {
            clearInterval(intervalId);
        };
    }, [props.tripsData]);



    const mapCenter = useMemo(() => {
        return { lng: 0, lat: 0 };
    }, []);



    return (
        <GenericPanelComp
            className={clsx(props.className, classes.root)}
            style={props.style}
            title={props.title}
            icon={props.icon}
            headerless={!props.title && !props.icon}
        >
            {props.tripsData.length > 0 && (
                <MapContainer
                    className={clsx(classes.mapContainer)}
                    style={props.mapContainerStyle}
                    center={mapCenter}
                    zoom={13}
                >
                    <TileLayer
                        attribution='<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
                        url={`${theme.mapTilesURL}?key=0FuuQf9TW9Dp1F7i0rsp`}
                    />
                    <MapFitBoundsHandler
                        positions={allLocations}
                        updateBoundOnce={true}
                    />
                    <LayersControl position="topright">
                        {/* Only create a layer for showing event markers if there is event data */}
                        {eventMetricsDefinition && (
                            <LayersControl.Overlay name={capitalizeAll(trans('mobility.label.incident', { count: 2 }))} checked>
                                <LayerGroup>
                                    {props.tripsData?.map((tripData) => {
                                        return tripData.events?.map((event) => {
                                            return (
                                                <EventMarker
                                                    key={`${event.timestamp}-${event.eventType}`}
                                                    projectId={props.projectId}
                                                    latitude={event.lat}
                                                    longitude={event.lon}
                                                    size={props.eventMarkerIconsSettingsByEventType?.[event.eventType]?.size ?? props.eventMarkerIconsSettings?.size}
                                                    tripData={tripData}
                                                    routeEvent={event}
                                                    eventMetricsDefinition={eventMetricsDefinition}
                                                    eventMetricsNameMapping={props.eventMetricsNameMapping}
                                                    eventTypeToEventStateMapping={props.eventTypeToEventStateMapping}
                                                    eventMarkerIconsSettings={props.eventMarkerIconsSettings}
                                                    eventMarkerIconsSettingsByEventType={props.eventMarkerIconsSettingsByEventType}
                                                />
                                            );
                                        });
                                    })}
                                </LayerGroup>
                            </LayersControl.Overlay>
                        )}
                        <LayersControl.Overlay name={capitalizeAll(trans('mobility.label.route', { count: 2 }))} checked>
                            <LayerGroup>
                                {props.tripsData?.map((tripData) => {
                                // If "showVehicleSpeed" is true then we will create 1 PolyLine for each segment with different color
                                    if (props.showVehicleSpeed && tripData.segments) {
                                        return tripData.segments.map((segment) => {
                                            const key = JSON.stringify(segment);
                                            return (
                                                <Polyline
                                                    key={key}
                                                    pathOptions={{
                                                        weight : props.lineThickness ?? MOBILITY_DEFAULT_ROUTE_LINE_THICKNESS,
                                                        opacity: 1,
                                                        lineCap: 'round',
                                                        color  : segment.color,
                                                    }}
                                                    positions={[
                                                        [segment.startLocation.lat, segment.startLocation.lon],
                                                        [segment.endLocation.lat, segment.endLocation.lon],
                                                    ]}
                                                />
                                            );
                                        });
                                    }

                                    // If "showVehicleSpeed" is false then we will create 1 PolyLine to show all the locations
                                    return (
                                        <Polyline
                                            key={`${tripData.tripInfo.vehicleEntityId}-${tripData.tripInfo.startTime}-${tripData.tripInfo.endTime}`}
                                            pathOptions={{
                                                weight : props.lineThickness,
                                                opacity: 1,
                                                lineCap: 'round',
                                                color  : props.speedColors.at(-1),
                                            }}
                                            positions={tripData.locations.map((loc) => {
                                                return {
                                                    lat: loc.lat, lng: loc.lon,
                                                };
                                            })}
                                        />
                                    );
                                })}
                                {selectedTripAndLocationObjects.location && (
                                    <VehicleMarker
                                        color={props.vehicleColor}
                                        size={props.vehicleSize}
                                        latitude={selectedTripAndLocationObjects.location.lat}
                                        longitude={selectedTripAndLocationObjects.location.lon}
                                        heading={selectedTripAndLocationObjects.location?.heading ?? 0}
                                    />
                                )}
                            </LayerGroup>
                        </LayersControl.Overlay>
                    </LayersControl>
                </MapContainer>
            )}

            {props.tripsData.length === 0 && (
                <GenericMessageBox
                    className={classes.noDataMessage}
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('mobility.label.trip', { count: 2 })),
                    })}
                />
            )}
        </GenericPanelComp>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'TripsRouteMapContainer', TripsRouteMapContainer,
);
